import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { CSVDownload } from 'react-csv'
import { useParams, useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { useReselector, useToggle } from '@/utils/sharedHooks'

import {
  PaginationPages,
  Button,
  Card,
  Text,
  Select,
  Input,
  Breadcrumb,
  Separator,
  DatePicker,
  Loader,
} from '@/ui'
import { TypographyStyles } from '@/styles'

import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { getUserFinancialAccounts } from '@/selectors/financeSelectors'
import { getUserById } from '@/selectors/user.selectors'
import {
  selectJournalEntriesByIds,
  selectJournalEntryTransactionsByIds,
} from '@/selectors/journalEntrySelectors'

import {
  fetchJournalEntries,
  fetchEntriesWithoutTransactions,
  FETCH_JOURNAL_ENTRY_TRANSACTIONS_KEY,
  FETCH_ENTRIES_WITHOUT_TRANSACTIONS_KEY,
  CREATE_JOURNAL_ENTRY_KEY,
} from '@/actions/admin/journalEntryActions'
import { adminFetchSingleUserIfNeeded } from '@/actions/admin/adminAllUsersActions'
import { fetchUserFinancialAccounts } from '@/actions/admin/adminFinancialAccountActions'
import { getAllTransactionCategories } from '@/features/reports/reports.selectors'
import { fetchAdminTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { JournalEntryTransactions } from '@/features/journal-entries/components/JETransactions'
import { EditJournalEntryModal } from '@/features/journal-entries/components/EditJournalEntryModal'
import {
  parseJournalEntryDataToCsv,
  getInitialDateFilters,
} from '@/features/journal-entries/service'

// TODO: Migrate to V2
import { Dimmer } from 'semantic-ui-react'

const PAGE_SIZE = 10

const AUTOMATED_FILTER_OPTIONS = [
  { value: 'manual', text: 'Manual' },
  { value: 'automated', text: 'Automated' },
]

export const AdminGeneralJournalPanel = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [{ count, transactionIds }, setJournalEntryData] = useState<{
    count: number
    transactionIds: number[]
  }>({ count: 0, transactionIds: [] })
  const [entryIdsWithoutTransaction, setEntryIdsWithoutTransaction] = useState<
    number[]
  >([])
  const [dateRange, setDateRange] = useState<[string, string]>(
    getInitialDateFilters()
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [csvData, setCsvData] = useState('')
  const [automatedFilter, setAutomatedFilter] = useState<string>()
  const [transactionIdFilter, setTransactionIdFilter] = useState<string>('')
  const [isCreating, toggleCreate] = useToggle()

  const { userId: userIdParam } = useParams<{ userId: string }>()
  const userId = Number(userIdParam)

  const user = useReselector(getUserById, userId)

  const pages = Math.ceil(count / PAGE_SIZE)
  const financialAccounts = useReselector(getUserFinancialAccounts, userId)
  const transactionCategories = useReselector(getAllTransactionCategories)
  const journalEntryTransactions = useReselector(
    selectJournalEntryTransactionsByIds,
    transactionIds
  )
  const entriesWithoutTransactions = useReselector(
    selectJournalEntriesByIds,
    entryIdsWithoutTransaction
  )

  const loading = useReselector(selectIsFetchingForKeys, [
    FETCH_JOURNAL_ENTRY_TRANSACTIONS_KEY,
    FETCH_ENTRIES_WITHOUT_TRANSACTIONS_KEY,
  ])

  const createLoading = useReselector(selectIsFetchingForKeys, [
    CREATE_JOURNAL_ENTRY_KEY,
  ])

  useEffect(() => {
    const fetch = async () => {
      const res = await fetchJournalEntries({
        userId,
        startDate: dateRange[0],
        endDate: dateRange[1],
        limit: PAGE_SIZE,
        page: currentPage,
        journalEntryType: automatedFilter ?? undefined,
        transactionId: transactionIdFilter
          ? parseInt(transactionIdFilter)
          : undefined,
      })(dispatch)
      const numPages = Math.ceil(res.count / PAGE_SIZE)
      // call endpoint to get journal entries without transactions if the user is on the last page
      if (
        !transactionIdFilter &&
        automatedFilter !== 'automated' &&
        currentPage === (numPages || 1)
      ) {
        const manualRes = await fetchEntriesWithoutTransactions({
          userId,
          startDate: dateRange[0],
          endDate: dateRange[1],
        })(dispatch)
        setEntryIdsWithoutTransaction(
          manualRes.map((journalEntry) => journalEntry.id)
        )
      } else {
        setEntryIdsWithoutTransaction([])
      }

      setJournalEntryData({
        count: res.count,
        transactionIds: filterNulls(
          res.transactions.map((transaction) => transaction.id)
        ),
      })
    }

    if (
      (dateRange[0] && dateRange[1] && createLoading === false) ||
      transactionIdFilter
    ) {
      fetch()
    } else {
      setJournalEntryData({ count: 0, transactionIds: [] })
      setEntryIdsWithoutTransaction([])
    }
  }, [
    dispatch,
    currentPage,
    dateRange,
    userId,
    transactionIdFilter,
    automatedFilter,
    createLoading,
  ])

  useEffect(() => {
    dispatch(adminFetchSingleUserIfNeeded(userId))
    dispatch(fetchUserFinancialAccounts(userId))
    dispatch(fetchAdminTransactionCategoriesIfNeeded(true))
  }, [dispatch, userId])

  const debouncedSetTransactionId = useMemo(
    () =>
      debounce((event) => {
        const value = event?.target.value
        setCurrentPage(1)
        setTransactionIdFilter(value)
      }, 1000),
    []
  )

  /**
   * This useEffect clears the csvData state variable in order to "unmount" the CSVDownload
   * component below. When the CSVDownload component is rendered in the DOM, it immediately
   * triggers a download of a CSV file with the given data. However, if the user wants to download
   * the CSV a second time, a download would not trigger if the CSVDownload component stayed
   * rendered, so setting the data to an empty string will unmount the CSVDownload component until
   * the next time the user presses "Download CSV", which will then populate the csvData state
   * variable, mount the CSVDownload component again, and start the download of the data again.
   */
  useEffect(() => {
    if (csvData) {
      setCsvData('')
    }
  }, [csvData])

  const handleDateChange = (dateRange: [string, string]) => {
    setCurrentPage(1)
    setDateRange(dateRange)
  }

  const handleAutomatedFilterChange = (value: string | undefined) => {
    setCurrentPage(1)
    setAutomatedFilter(value)
  }

  const handleDownloadCsv = async () => {
    const { transactions } =
      (await fetchJournalEntries({
        userId,
        startDate: dateRange[0],
        endDate: dateRange[1],
      })(dispatch)) ?? []

    if (transactions.length) {
      setCsvData(
        parseJournalEntryDataToCsv({
          journalEntryTransactions: transactions,
          financialAccounts,
          transactionCategories,
          sortFunction: (
            { transaction: { date: date1 } },
            { transaction: { date: date2 } }
          ) =>
            DateTime.fromISO(date1 ?? '')
              .diff(DateTime.fromISO(date2 ?? ''))
              .toMillis(),
        })
      )
    }
  }

  const renderBreadcrumb = () => {
    const usersLink = '/admin/finances/list-users'
    return (
      <Breadcrumb>
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate('/')}>
              Dashboard
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate(usersLink)}>
              Users
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Link
              onClick={() => navigate(`/admin/finances/records/${userId}`)}
            >
              {`${user?.firstName} ${user?.lastName}`}
            </Breadcrumb.Link>
          </Breadcrumb.Item>
          <Breadcrumb.Separator />
          <Breadcrumb.Item>
            <Breadcrumb.Page>General Journal</Breadcrumb.Page>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>
    )
  }

  return (
    <div>
      {renderBreadcrumb()}

      <Separator />

      <Card>
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between">
            <div className="w-1/2">
              <Text variant={TypographyStyles.H1}>General Journal</Text>
              <Text variant={TypographyStyles.BODY_LG}>
                {user?.financialProfile?.businessName ??
                  `${user?.firstName} ${user?.lastName}`}
              </Text>
            </div>
            <div className="w-1/4">
              <Button
                variant="secondary"
                onClick={toggleCreate}
                className="w-full"
              >
                Create Journal Entry
              </Button>
              {isCreating && (
                <EditJournalEntryModal
                  open={isCreating}
                  onClose={toggleCreate}
                />
              )}
            </div>
            <div className="w-1/4 ml-4">
              <Button
                disabled={!transactionIds.length}
                onClick={handleDownloadCsv}
                className="w-full"
              >
                Download CSV
              </Button>
              {csvData ? <CSVDownload data={csvData} target="_blank" /> : null}
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="w-1/3">
              <DatePicker
                clearable
                onChange={handleDateChange}
                style={{ width: 270 }}
                endDate={dateRange[1]}
                selectsRange
                startDate={dateRange[0]}
                value={dateRange}
              />
            </div>
            <div className="w-1/3">
              <Select
                placeholder="Filter by manual/auto"
                value={automatedFilter}
                onValueChange={handleAutomatedFilterChange}
                options={AUTOMATED_FILTER_OPTIONS}
                clearable
              />
            </div>
            <div className="w-1/3 ml-4">
              <Input
                placeholder="Filter by transaction ID"
                onChange={debouncedSetTransactionId}
                type="number"
                className="w-full"
              />
            </div>
          </div>
          <Separator />
          {loading ? (
            <Dimmer active inverted>
              <Loader>Loading journal entries</Loader>
            </Dimmer>
          ) : (
            <JournalEntryTransactions
              userId={userId}
              transactions={journalEntryTransactions}
              entriesWithoutTransactions={entriesWithoutTransactions}
              dateRange={dateRange}
            />
          )}
          {pages > 1 && (
            <div className="flex justify-center items-center">
              <PaginationPages
                currentPage={currentPage}
                onPageClick={(page: number) => setCurrentPage(page)}
                pages={pages}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
